import { useEffect } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useAppContext } from 'context/AppContext/AppContext'
import RequestStatus from 'components/RequestStatus/RequestStatus'
import { excelExt } from 'config/extensions'
import { useMutation } from '@tanstack/react-query'
import { axiosPost } from 'connectors/axiosPost'
import type { GlobalBarcodesFormValues } from './GlobalBarcodesForm.types'
import { useNavigate } from 'react-router-dom'
import { Paper, Typography, Divider } from '@mui/material'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import FileUpload from 'components/FileUpload'
import { useAlert } from 'context/AlertContext'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import Input from 'components/Input'
import PageLoader from 'components/PageLoader'

const GlobalBarcodesForm = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { changeMessage } = useAlert()
  const { markets, isMarketsLoading } = useAppContext()
  const { token, refreshToken } = useAuthContext()

  const validationSchema = Yup.object().shape({})

  const defaultValues: GlobalBarcodesFormValues = {
    file: null,
    backup: '',
  }

  const { mutate, data, status, reset, isLoading } = useMutation(
    async (codes: FormData) => await axiosPost<any>(token, refreshToken, 'barcodes/import', codes)
  )

  const { handleSubmit, control } = useForm<GlobalBarcodesFormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    if (data !== undefined && 'error' in data) {
      changeMessage(data.error.data?.message, 'error', reset)
    }
    if (data !== undefined && status === 'success' && !('error' in data)) {
      changeMessage(t('forms.importSucess'), 'success', reset)
    }
  }, [data, status])

  const onHandleSubmit = (data: GlobalBarcodesFormValues): void => {
    const fd = new FormData()

    if (data.file !== null) {
      fd.append('file', data.file)
    }
    if (data.backup !== '') {
      fd.append('backup', data.backup)
    }

    mutate(fd)
  }

  if (markets === undefined || 'error' in markets) {
    return <RequestStatus data={markets} isLoading={isMarketsLoading} />
  }

  return (
    <>
      <Typography variant="h4" component="h1" mb={2}>
        {t('common.addField', { field: t('navigation.globalBarcodes') })}
      </Typography>
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit(onHandleSubmit)} noValidate>
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={() => {
              navigate('/barcodes')
            }}
            startIcon={<ChevronLeft />}
          >
            {t('common.backListText')}
          </Button>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ maxWidth: 640, width: '100%' }}>
            <FileUpload
              control={control}
              extensions={excelExt}
              type="file"
              name="file"
              label={t('forms.fileContains', { field: t('navigation.globalBarcodes') })}
            />
            <Box>
              <Input
                control={control}
                label="backup"
                name="backup"
                type="switch"
                textInputProps={{ size: 'small', sx: { mx: 0 } }}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => {
                navigate('/barcodes')
              }}
              startIcon={<ChevronLeft />}
            >
              {t('common.backListText')}
            </Button>
            <Button type="submit" color="success" variant="contained">
              {t('common.add')}
            </Button>
          </Box>
        </form>
      </Paper>
      {isLoading && <PageLoader />}
    </>
  )
}

export default GlobalBarcodesForm
